import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  ImportJobStatus,
  ImportTargetList,
  ImportProgressPayload,
  ImportCompletePayload,
  ImportFailedCard,
} from 'types/interfaces/import';
import type { RootState } from 'store';

// --- State interfaces ---

interface ImportJobState {
  jobId: string | null;
  status: ImportJobStatus | 'idle';
  targetList: ImportTargetList | null;
  totalRows: number;
  processedRows: number;
  matched: number;
  skipped: number;
  failed: number;
  failedCards: ImportFailedCard[];
  skipReasons: Record<string, number>;
  error: string | null;
}

interface ImportSliceState {
  job: ImportJobState;
  isPillVisible: boolean;
  isPillDismissed: boolean;
  isModalOpen: boolean;
  // Socket connection state (follows activitySlice pattern)
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
  reconnectAttempts: number;
}

// --- Initial state ---

const initialJobState: ImportJobState = {
  jobId: null,
  status: 'idle',
  targetList: null,
  totalRows: 0,
  processedRows: 0,
  matched: 0,
  skipped: 0,
  failed: 0,
  failedCards: [],
  skipReasons: {},
  error: null,
};

const initialState: ImportSliceState = {
  job: { ...initialJobState },
  isPillVisible: false,
  isPillDismissed: false,
  isModalOpen: false,
  isConnected: false,
  isConnecting: false,
  connectionError: null,
  reconnectAttempts: 0,
};

// --- Slice ---

const importSlice = createSlice({
  name: 'import',
  initialState,
  reducers: {
    // Job lifecycle
    importJobStarted: (
      state,
      action: PayloadAction<{ jobId: string; totalRows: number; targetList: ImportTargetList }>
    ) => {
      state.job = {
        ...initialJobState,
        jobId: action.payload.jobId,
        status: 'pending',
        targetList: action.payload.targetList,
        totalRows: action.payload.totalRows,
      };
      state.isPillVisible = true;
      state.isPillDismissed = false;
    },

    importProgress: (state, action: PayloadAction<ImportProgressPayload>) => {
      // Only process if jobId matches
      if (state.job.jobId !== action.payload.jobId) return;
      state.job.status = 'processing';
      state.job.processedRows = action.payload.processedRows;
      state.job.totalRows = action.payload.totalRows;
      state.job.matched = action.payload.matched;
      state.job.skipped = action.payload.skipped;
      state.job.failed = action.payload.failed;
    },

    importComplete: (state, action: PayloadAction<ImportCompletePayload>) => {
      // Only process if jobId matches
      if (state.job.jobId !== action.payload.jobId) return;
      state.job.status = action.payload.status;
      state.job.processedRows = action.payload.processedRows;
      state.job.totalRows = action.payload.totalRows;
      state.job.matched = action.payload.matched;
      state.job.skipped = action.payload.skipped;
      state.job.failed = action.payload.failed;
      state.job.failedCards = action.payload.failedCards;
      state.job.skipReasons = action.payload.skipReasons ?? {};
      state.job.error = action.payload.error ?? null;
    },

    // UI controls
    dismissImportPill: (state) => {
      state.isPillDismissed = true;
    },

    resetImportJob: (state) => {
      state.job = { ...initialJobState };
      state.isPillVisible = false;
      state.isPillDismissed = false;
      state.isModalOpen = false;
    },

    setImportModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isModalOpen = action.payload;
    },

    // Socket connection state (follows activitySlice pattern)
    importSocketConnecting: (state) => {
      state.isConnecting = true;
      state.connectionError = null;
    },
    importSocketConnected: (state) => {
      state.isConnected = true;
      state.isConnecting = false;
      state.connectionError = null;
      state.reconnectAttempts = 0;
    },
    importSocketDisconnected: (state) => {
      state.isConnected = false;
      state.isConnecting = false;
    },
    importSocketError: (state, action: PayloadAction<string>) => {
      state.isConnected = false;
      state.isConnecting = false;
      state.connectionError = action.payload;
    },
    importSocketReconnectAttempt: (state) => {
      state.reconnectAttempts += 1;
    },
    resetImportSocketState: (state) => {
      state.isConnected = false;
      state.isConnecting = false;
      state.connectionError = null;
      state.reconnectAttempts = 0;
    },
  },
});

// --- Selectors ---

export const selectImportJob = (state: RootState) => state.import.job;

export const selectImportProgress = (state: RootState) => {
  const { processedRows, totalRows } = state.import.job;
  return {
    processedRows,
    totalRows,
    percentage: totalRows > 0 ? Math.round((processedRows / totalRows) * 100) : 0,
  };
};

export const selectIsImporting = (state: RootState) => {
  const { status } = state.import.job;
  return status === 'pending' || status === 'processing';
};

export const selectImportPillState = (state: RootState) => {
  const { job, isPillVisible, isPillDismissed } = state.import;
  const { totalRows, processedRows } = job;
  const percentage = totalRows > 0 ? Math.round((processedRows / totalRows) * 100) : 0;

  return {
    visible: isPillVisible && !isPillDismissed,
    status: job.status,
    percentage,
    targetList: job.targetList,
  };
};

// --- Exports ---

export const {
  importJobStarted,
  importProgress,
  importComplete,
  dismissImportPill,
  resetImportJob,
  setImportModalOpen,
  importSocketConnecting,
  importSocketConnected,
  importSocketDisconnected,
  importSocketError,
  importSocketReconnectAttempt,
  resetImportSocketState,
} = importSlice.actions;

export default importSlice.reducer;
